import React, {useState, useMemo, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {Platform, KeyboardAvoidingView, ScrollView} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {RFValue} from 'react-native-responsive-fontsize';

import {Header} from '../../../components/Interface/Headers/Header';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {Input} from '../../../components/Interface/Forms/Inputs/Input';
import {Picker} from '../../../components/Interface/Forms/Inputs/Picker';
import {Loading} from '../../../components/Interface/Loadings/Loading';

import {salvarEnderecoCredito} from '../../../services/creditoMova';
import {estados} from '../../../utils/estados';
import useCep from '../../../context/cep';

import {
  Container,
  Content,
  Footer,
  Form,
  InputGroup,
  InputGrid,
} from './styles';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';

type RouteParams = {
  simulacao: any;
  installments_option_id: string;
  selectedOption: any;
};

export function EmprestimoEndereco() {
  const navigation = useNavigation();
  const route = useRoute();
  const {buscaCep} = useCep();
  const params = route.params as RouteParams;

  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('select');
  const [loading, setLoading] = useState(false);

  const formEnabled = useMemo(
    () =>
      cep.length === 8 &&
      logradouro.length > 4 &&
      numero.length > 0 &&
      bairro.length > 2 &&
      cidade.length > 2 &&
      uf !== 'select' &&
      uf.length === 2,
    [cep, logradouro, numero, bairro, cidade, uf],
  );

useEffect(() => {
  analytics().logEvent('emprestimo_tela_endereco');
}, []);


  const handleChangeCep = async (_: string, extracted = '') => {
    setCep(extracted);
    if (extracted.length === 8) {
      try {
        const {data} = await buscaCep(extracted);
        if (data?.erro) {
          return;
        }
        setLogradouro(data.logradouro || '');
        setBairro(data.bairro || '');
        setCidade(data.localidade || '');
        setUf(data.uf || 'select');
        setComplemento(data.complemento || '');
      } catch {}
    }
  };

const handleContinue = async () => {
  analytics().logEvent('emprestimo_clicou_continuar_endereco');
  try {
    setLoading(true);
    const enderecoResponse = await salvarEnderecoCredito({
        cep,
        logradouro,
        numero,
        complemento: complemento || undefined,
        bairro,
        cidade,
        uf,
      });

      (navigation as any).navigate('EmprestimoDomicilio', {
        ...params,
        endereco_id: enderecoResponse.id,
      });
    } catch (error: any) {
      const body = error?.response?.data;
      const errorMessage: string | undefined =
        body?.error || body?.msg || body?.message || undefined;
      navigation.navigate('ConfirmationScreen', {
        title: 'Ocorreu um problema',

        text:
          errorMessage ||
          'Ocorreu um erro ao prosseguir com sua solicitação. Por favor, tente novamente mais tarde.',
        buttonAction: () => navigation.goBack(),
        closeIconAction: () => navigation.navigate('Initial'),
        buttonText: 'Tentar novamente',
        type: 'error',
        showSupport: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EmprestimoEligibilityGate />
      {loading && <Loading />}
      <SafeAreaView edges={['bottom']} style={{flex: 1}}>
        <Header icon="chevron-left" />

        <KeyboardAvoidingView
          style={{flex: 1}}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingBottom: Platform.OS === 'ios' ? RFValue(20) : RFValue(40),
            }}>
            <Container>
              <Content>
                <Titles
                  title="Endereço"
                  subtitle="Informe o seu endereço para continuar com o empréstimo."
                />

                <Form>
                  <InputGrid>
                    <InputGroup style={{flex: 1}}>
                      <Input
                        labelText="CEP"
                        mask="[00000]-[000]"
                        placeholder="00000-000"
                        value={cep}
                        keyboardType="number-pad"
                        onChangeText={(_, extracted) =>
                          handleChangeCep(_, extracted)
                        }
                      />
                    </InputGroup>
                    <InputGroup style={{flex: 1}}>
                      <Picker
                        label="Estado"
                        value={uf}
                        placeholder={{
                          label: 'Selecione',
                          value: 'select',
                        }}
                        onChangeValue={setUf}
                        items={estados}
                      />
                    </InputGroup>
                  </InputGrid>

                  <InputGroup>
                    <Input
                      labelText="Logradouro"
                      placeholder="Rua, Avenida, etc."
                      value={logradouro}
                      onChangeText={setLogradouro}
                    />
                  </InputGroup>

                  <InputGrid>
                    <InputGroup style={{flex: 1}}>
                      <Input
                        labelText="Número"
                        placeholder="Nº"
                        value={numero}
                        keyboardType="number-pad"
                        onChangeText={setNumero}
                      />
                    </InputGroup>
                    <InputGroup style={{flex: 1}}>
                      <Input
                        labelText="Complemento"
                        placeholder="Opcional"
                        value={complemento}
                        onChangeText={setComplemento}
                      />
                    </InputGroup>
                  </InputGrid>

                  <InputGroup>
                    <Input
                      labelText="Bairro"
                      placeholder="Bairro"
                      value={bairro}
                      onChangeText={setBairro}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Input
                      labelText="Cidade"
                      placeholder="Cidade"
                      value={cidade}
                      onChangeText={setCidade}
                    />
                  </InputGroup>
                </Form>
              </Content>

              <Footer>
                <Button
                  title={'Continuar'}
                  onPress={handleContinue}
                  enabled={formEnabled && !loading}
                />
              </Footer>
            </Container>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}
